import { v4 as uuidv4 } from 'uuid';

export enum GRIPPER_TYPE {
	VACUUM = 'Vacuum',
	ONROBOT_FGP = 'OnRobot FGP',
}

export enum SENSOR_TYPE {
	DigitalSensor = 'Digital Sensor',
	PressureSensor = 'Pressure Sensor',
	OnRobotForceFeedback = 'OnRobot FGP',
}

function createDefaultTcp(): Tcp {
	return {
		offset: { X: 0, Y: 0, Z: 100, Rx: 0, Ry: 0, Rz: 0 },
		sensors: [
			{
				id: 1,
				pin: 0,
				ReverseLogic: false,
				sensorType: 'Digital Sensor',
			},
		],
		actuators: [
			{
				id: 1,
				pin: 1,
				ReverseLogic: false,
			},
		],
	};
}

export function gripperReducer(
	state: GripperState,
	action: GripperAction
): GripperState {
	switch (action.type) {
		case 'SET_INITIAL': {
			// Ensure all grippers have a default main_valve if missing
			const patchedGrippers = Object.entries(action.grippers).reduce(
				(acc, [id, gripper]) => {
					acc[id] = {
						...gripper,
						main_valve: gripper.main_valve ?? {
							id: 1,
							pin: 1,
							ReverseLogic: false,
							enabled: false,
						},
					};
					return acc;
				},
				{} as Record<string, Gripper>
			);

			const firstId = Object.keys(patchedGrippers)[0] || null;
			return {
				grippers: patchedGrippers,
				original: JSON.parse(JSON.stringify(patchedGrippers)),
				selectedId: firstId,
			};
		}

		case 'SELECT':
			return { ...state, selectedId: action.id };

		case 'ADD': {
			const id = uuidv4();
			const name = `Gripper ${Object.keys(state.grippers).length}`;
			const newGripper: Gripper = {
				id,
				name,
				type: GRIPPER_TYPE.VACUUM,
				active: Object.keys(state.grippers).length === 0,
				weight: 0,
				cog: { x: 0, y: 0, z: 100 },
				width: 200,
				length: 300,
				wait_grip: 0.3,
				wait_release: 0.3,
				timeout: 10,
				main_valve: {
					id: 1,
					pin: 1,
					ReverseLogic: false,
					enabled: false,
				},
				last_pick: null,
				tcps: [createDefaultTcp(), createDefaultTcp()],
			};

			return {
				...state,
				grippers: { [id]: newGripper, ...state.grippers },
				selectedId: id,
			};
		}

		case 'UPDATE': {
			const updated = {
				...state.grippers[action.id],
				...action.changes,
			};
			return {
				...state,
				grippers: { ...state.grippers, [action.id]: updated },
			};
		}

		case 'RENAME': {
			const gripper = state.grippers[action.id];
			if (!gripper) return state;
			return {
				...state,
				grippers: {
					...state.grippers,
					[action.id]: { ...gripper, name: action.newName },
				},
			};
		}

		case 'DELETE': {
			const { [action.id]: removed, ...remaining } = state.grippers;

			let updated = { ...remaining };
			const stillHasDefault = Object.values(updated).some(g => g.active);

			// Reassign default if we deleted the only active one
			if (removed?.active && !stillHasDefault) {
				const firstKey = Object.keys(updated)[0];
				if (firstKey) updated[firstKey] = { ...updated[firstKey], active: true };
			}

			return {
				...state,
				grippers: updated,
				selectedId: Object.keys(updated)[0] || null,
			};
		}

		case 'MAKE_DEFAULT': {
			const updated: Record<string, Gripper> = {};
			for (const [id, g] of Object.entries(state.grippers)) {
				updated[id] = { ...g, active: id === action.id };
			}
			return { ...state, grippers: updated };
		}

		case 'CANCEL_CHANGES':
			return {
				grippers: JSON.parse(JSON.stringify(state.original)),
				original: state.original,
				selectedId: Object.keys(state.original)[0] || null,
			};

		default:
			return state;
	}
}

type Cog = { x: number; y: number; z: number };
type Offset = {
	X: number;
	Y: number;
	Z: number;
	Rx: number;
	Ry: number;
	Rz: number;
};
export type Sensor = {
	id: number;
	pin: number;
	ReverseLogic: boolean;
	sensorType: string;
};
type Actuator = { id: number; pin: number; ReverseLogic: boolean };
type Tcp = { offset: Offset; sensors: Sensor[]; actuators: Actuator[] };

type IOConfig = {
	id: number;
	pin: number;
	ReverseLogic: boolean;
	enabled?: boolean;
};

export type Gripper = {
	id: string;
	name: string;
	type: GRIPPER_TYPE;
	active: boolean;
	weight: number;
	cog: Cog;
	width: number;
	length: number;
	tcps: Tcp[];
	wait_grip: number;
	wait_release: number;
	timeout: number;
	last_pick: string | null;
	main_valve?: IOConfig;
};

export type GripperState = {
	grippers: Record<string, Gripper>; // key by ID
	selectedId: string | null;
	original: Record<string, Gripper>; // for cancel/reset comparison
};

export type GripperAction =
	| { type: 'SET_INITIAL'; grippers: Record<string, Gripper> }
	| { type: 'SELECT'; id: string }
	| { type: 'ADD' }
	| { type: 'UPDATE'; id: string; changes: Partial<Gripper> }
	| { type: 'RENAME'; id: string; newName: string }
	| { type: 'DELETE'; id: string }
	| { type: 'MAKE_DEFAULT'; id: string }
	| { type: 'CANCEL_CHANGES' };
